import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, forkJoin, of, switchMap } from "rxjs";
import { environment } from "src/environments/environment";
import { EnigmeAdminDTO } from "../models/enigmeAdminDTO.model";
import { EnigmeAdminModel } from "../models/enigmeAdmin.model";
import { IssueAdminDTO } from "../models/issueAdminDTO.model";
import { EnigmeSiteAdminModel } from "../models/enigmeSiteAdmin.model";
import { CtfPartyFormModel } from "../models/ctfPartyFormModel";
import { AssetModel, CreateAssetRequest } from "../models/asset.model";

@Injectable({
  providedIn: "root",
})
export class AdminService {
  private readonly adminEndpoint = environment.baseUrl + "/admin/enigmes";
  private readonly issuesEndpoint = environment.baseUrl + "/admin/issues";
  private readonly partyEndpoint = environment.baseUrl + "/admin/party";
  private readonly assetsEndpoint =
    environment.baseUrl + "/admin/enigmes/assets";
  private readonly assetEndpoint = environment.baseUrl + "/admin/asset";
  private readonly cacheEndpoint = environment.baseUrl + "/admin/cache";

  constructor(private httpClient: HttpClient) {}

  /**
   * Récupération de la liste de toutes les enigmes
   */
  getAllEnigmes(): Observable<EnigmeAdminDTO> {
    return this.httpClient.get<EnigmeAdminDTO>(this.adminEndpoint);
  }

  /**
   * Récupération d'une enigme spécifique
   */
  getEnigmeById(id: number): Observable<EnigmeAdminModel> {
    return this.httpClient.get<EnigmeAdminModel>(`${this.adminEndpoint}/${id}`);
  }

  /**
   * Modification d'une enigme
   */
  updateEnigme(data: EnigmeAdminModel): Observable<EnigmeAdminModel> {
    return this.httpClient.put<EnigmeAdminModel>(this.adminEndpoint, data);
  }

  /**
   * Création d'une enigme
   */
  createEnigme(data: EnigmeAdminModel): Observable<EnigmeAdminModel> {
    return this.httpClient.post<EnigmeAdminModel>(this.adminEndpoint, data);
  }

  /**
   * Récupération de toutes les issues
   */
  getAllIssues(): Observable<IssueAdminDTO[]> {
    return this.httpClient.get<IssueAdminDTO[]>(this.issuesEndpoint);
  }

  /**
   * Récupération des issues pour une enigme spécifique
   */
  getIssuesByEnigmeId(enigmeId: number): Observable<IssueAdminDTO[]> {
    return this.httpClient.get<IssueAdminDTO[]>(
      `${this.issuesEndpoint}?enigmeId=${enigmeId}`,
    );
  }

  /**
   * Suppression d'une issue
   */
  deleteIssue(issueId: number): Observable<IssueAdminDTO> {
    return this.httpClient.delete<IssueAdminDTO>(
      `${this.issuesEndpoint}/${issueId}`,
    );
  }

  /**
   * Récupération par site des énigmes résolues après une date donnée
   */
  getCTFsDoneBySite(
    data: CtfPartyFormModel,
  ): Observable<EnigmeSiteAdminModel[]> {
    return this.httpClient.post<EnigmeSiteAdminModel[]>(
      this.partyEndpoint,
      data,
    );
  }

  getAllAssets(): Observable<AssetModel[]> {
    return this.httpClient.get<AssetModel[]>(this.assetsEndpoint);
  }

  getAssetById(id: number): Observable<AssetModel> {
    return this.httpClient.get<AssetModel>(`${this.assetEndpoint}/${id}`);
  }

  uploadFile(file: File, fileName: string): Observable<AssetModel> {
    const formData = new FormData();
    formData.append("file", file);

    return this.httpClient.post<AssetModel>(
      `${environment.baseUrl}/admin/file/${encodeURIComponent(fileName)}`,
      formData,
    );
  }

  createAsset(data: CreateAssetRequest): Observable<AssetModel> {
    return this.httpClient.post<AssetModel>(this.assetEndpoint, data);
  }

  getAssetLinks(): Observable<any[]> {
    return this.httpClient.get<any[]>(`${environment.baseUrl}/admin/link`);
  }

  createAssetLink(assetId: number, enigmeId: number): Observable<any> {
    const linkData = {
      id_enigme: enigmeId,
      id_asset: assetId,
    };
    return this.httpClient.post(`${environment.baseUrl}/admin/link`, linkData);
  }

  deleteAsset(id: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.assetEndpoint}/${id}`);
  }

  deleteAssetLink(linkId: number): Observable<any> {
    return this.httpClient.delete(
      `${environment.baseUrl}/admin/link/${linkId}`,
    );
  }

  /** Réconcilie les assets liés à un CTF (ajout/suppression de liens) avec la sélection faite dans le formulaire. */
  syncCtfAssetLinks(
    ctfId: number,
    newAssetIds: number[],
    initialAssetIds: number[],
  ): Observable<any> {
    const toAdd = newAssetIds.filter((id) => !initialAssetIds.includes(id));
    const toRemove = initialAssetIds.filter((id) => !newAssetIds.includes(id));
    if (toAdd.length === 0 && toRemove.length === 0) {
      return of(null);
    }
    return this.getAssetLinks().pipe(
      switchMap((links) => {
        const deleteRequests = links
          .filter(
            (link) => link.id_enigme === ctfId && toRemove.includes(link.id_asset),
          )
          .map((link) => this.deleteAssetLink(link.id));
        const addRequests = toAdd.map((assetId) =>
          this.createAssetLink(assetId, ctfId),
        );
        const requests = [...deleteRequests, ...addRequests];
        return requests.length ? forkJoin(requests) : of(null);
      }),
    );
  }

  deleteAssetFromMinio(fileName: string): Observable<void> {
    return this.httpClient.delete<void>(
      `${environment.baseUrl}/admin/file/${encodeURIComponent(fileName)}`,
    );
  }

  refreshCache(): Observable<void> {
    return this.httpClient.post<void>(this.cacheEndpoint, null);
  }
}
